import React, { useState } from 'react';
import { Pricing, PricingCreate, PricingUpdate } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { StorePricing, StoreTax } from '../../../services/configService';
import { formatCurrency } from '../../../utils/formatters';
import { getTodayString, formatDate } from '../../../utils/dateUtils';
import EffectiveSummary from './EffectiveSummary';

// ============================================================================
// SitePricingFormV2 - New pricing form with clear hierarchy display
// Store Base Rate (read-only) + Site Special Pricing (date-range overrides)
// ============================================================================

interface SitePricingFormV2Props {
  siteId: number;
  siteTypeId?: number;
  pricing: Pricing[];
  storePricing: StorePricing[];
  storeTaxes: StoreTax[];
  onChange: (pricing: Pricing[]) => void;
}

interface PricingFormData {
  price: number;
  effective_date: string;
  end_date: string;
  discount: number | undefined;
  active: boolean;
}

const emptyPricingForm: PricingFormData = {
  price: 0,
  effective_date: '',
  end_date: '',
  discount: undefined,
  active: true
};

const SitePricingFormV2: React.FC<SitePricingFormV2Props> = ({
  siteId,
  siteTypeId,
  pricing,
  storePricing,
  storeTaxes,
  onChange
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<PricingFormData>(emptyPricingForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get store base rate based on site type
  const getStoreBaseRate = (): { price: number; description: string; siteTypeName?: string } | null => {
    if (!storePricing || storePricing.length === 0) return null;

    // First, try to find a rate matching the site's type_id
    if (siteTypeId) {
      const matchingRate = storePricing.find(r => r.site_type_id === siteTypeId && r.active);
      if (matchingRate) {
        return {
          price: matchingRate.price,
          description: matchingRate.site_type_name || 'Site Type Rate',
          siteTypeName: matchingRate.site_type_name
        };
      }
    }

    // Fall back to default rate (no site_type_id)
    const defaultRate = storePricing.find(r => !r.site_type_id && r.active);
    if (defaultRate) {
      return { price: defaultRate.price, description: 'Default Store Rate' };
    }

    return null;
  };

  const storeBaseRate = getStoreBaseRate();
  const activeTaxes = storeTaxes?.filter(t => t.active) || [];

  // Format date range for display. Pricing dates are calendar days
  // (YYYY-MM-DD), so they go through dateUtils to stay on the local day.
  const formatDateRange = (start?: string, end?: string): string => {
    if (!start && !end) return 'Always';
    const startStr = start ? formatDate(start) : 'Start';
    const endStr = end ? formatDate(end) : 'Ongoing';
    return `${startStr} - ${endStr}`;
  };

  // Get current effective rate (checks if any site special pricing is active today)
  const getEffectiveRate = (): { price: number; source: 'store' | 'site'; description: string } => {
    const today = getTodayString();

    // Check for active site special pricing for today
    const activeSpecialPricing = pricing.find(p => {
      if (!p.active) return false;
      const startOk = !p.effective_date || p.effective_date <= today;
      const endOk = !p.end_date || p.end_date >= today;
      return startOk && endOk;
    });

    if (activeSpecialPricing) {
      return {
        price: activeSpecialPricing.price,
        source: 'site',
        description: formatDateRange(activeSpecialPricing.effective_date, activeSpecialPricing.end_date)
      };
    }

    // Fall back to store base rate
    if (storeBaseRate) {
      return {
        price: storeBaseRate.price,
        source: 'store',
        description: storeBaseRate.description
      };
    }

    return { price: 0, source: 'store', description: 'No pricing configured' };
  };

  const effectiveRate = getEffectiveRate();

  const handleAddNew = () => {
    // Pre-populate with store base rate as starting point
    if (storeBaseRate) {
      setFormData({
        ...emptyPricingForm,
        price: storeBaseRate.price
      });
    } else {
      setFormData(emptyPricingForm);
    }
    setEditingId(null);
    setShowAddForm(true);
    setError(null);
  };

  const handleEdit = (pricingItem: Pricing) => {
    setFormData({
      price: pricingItem.price,
      effective_date: pricingItem.effective_date || '',
      end_date: pricingItem.end_date || '',
      discount: pricingItem.discount,
      active: pricingItem.active
    });
    setEditingId(pricingItem.id);
    setShowAddForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setFormData(emptyPricingForm);
    setEditingId(null);
    setShowAddForm(false);
    setError(null);
  };

  const handleSave = async () => {
    if (formData.price <= 0) {
      setError('Rate must be greater than 0');
      return;
    }

    if (!formData.effective_date || !formData.end_date) {
      setError('Both start date and end date are required for special pricing');
      return;
    }

    if (formData.effective_date > formData.end_date) {
      setError('End date must be after start date');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingId) {
        // Update existing
        const updateData: PricingUpdate = {
          price: formData.price,
          effective_date: formData.effective_date,
          end_date: formData.end_date,
          discount: formData.discount,
          active: formData.active
        };
        const updated = await siteService.updatePricing(editingId, updateData);
        onChange(pricing.map(p => p.id === editingId ? updated : p));
      } else {
        // Create new
        const createData: PricingCreate = {
          site_id: siteId,
          price: formData.price,
          effective_date: formData.effective_date,
          end_date: formData.end_date,
          discount: formData.discount,
          active: formData.active
        };
        const created = await siteService.createPricing(createData);
        onChange([...pricing, created]);
      }

      handleCancel();
    } catch (err) {
      setError('Failed to save pricing');
      console.error('Error saving pricing:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pricingId: number) => {
    if (!window.confirm('Are you sure you want to delete this special pricing period?')) {
      return;
    }

    try {
      await siteService.deletePricing(pricingId);
      onChange(pricing.filter(p => p.id !== pricingId));
    } catch (err) {
      console.error('Error deleting pricing:', err);
    }
  };

  // Check if a pricing period is currently active
  const isPricingActive = (p: Pricing): boolean => {
    if (!p.active) return false;
    const today = getTodayString();
    const startOk = !p.effective_date || p.effective_date <= today;
    const endOk = !p.end_date || p.end_date >= today;
    return startOk && endOk;
  };

  return (
    <div className="site-pricing-form-v2">
      {/* Store Taxes Info Section */}
      {activeTaxes.length > 0 && (
        <div className="store-info-section">
          <h4>Applicable Store Taxes</h4>
          <p className="hierarchy-section-description">
            These taxes are configured at the store level and apply to all reservations.
          </p>
          <ul className="tax-list">
            {activeTaxes.map(tax => (
              <li key={tax.id}>
                <span className="tax-name">{tax.tax_name}</span>
                <span className="tax-rate">
                  {tax.rate_type === 'percentage' ? `${tax.rate_value}%` : formatCurrency(tax.rate_value)}
                </span>
                <span className="tax-unit">per {tax.unit_type}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Store Base Rate Section */}
      <div className="hierarchy-section store-defaults-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Store Base Rate</h4>
          <span className="source-badge store">Store Default</span>
        </div>
        <p className="hierarchy-section-description">
          This is the default rate when no special pricing is active.
          {siteTypeId && storeBaseRate?.siteTypeName && (
            <> Based on site type: <strong>{storeBaseRate.siteTypeName}</strong></>
          )}
        </p>

        {storeBaseRate ? (
          <div className="hierarchy-items-list">
            <div className="hierarchy-item store-default-item">
              <div className="hierarchy-item-content">
                <span className="pricing-rate">{formatCurrency(storeBaseRate.price)}/night</span>
                <span className="pricing-description">{storeBaseRate.description}</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="hierarchy-empty-message">
            No store base rate configured. Please configure store pricing first.
          </p>
        )}
      </div>

      {/* Site Special Pricing Section */}
      <div className="hierarchy-section site-overrides-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Site Special Pricing</h4>
          <span className="source-badge site">Site Specific</span>
        </div>
        <p className="hierarchy-section-description">
          Create special rates for specific date ranges. These override the store base rate during the specified period.
        </p>

        {pricing.length === 0 ? (
          <p className="hierarchy-empty-message">
            No special pricing configured. This site uses the store base rate.
          </p>
        ) : (
          <div className="hierarchy-items-list">
            {[...pricing]
              .sort((a, b) => {
                // Sort by effective_date, then by active status
                if (!a.effective_date) return 1;
                if (!b.effective_date) return -1;
                return a.effective_date.localeCompare(b.effective_date);
              })
              .map(item => (
                <div
                  key={item.id}
                  className={`hierarchy-item site-override-item ${isPricingActive(item) ? 'currently-active' : ''} ${!item.active ? 'inactive' : ''}`}
                >
                  <div className="hierarchy-item-content">
                    <div className="pricing-item-main">
                      <span className="pricing-date-icon">📅</span>
                      <span className="pricing-date-range">
                        {formatDateRange(item.effective_date, item.end_date)}
                      </span>
                      <span className="pricing-rate">{formatCurrency(item.price)}/night</span>
                      {item.discount && item.discount > 0 && (
                        <span className="pricing-discount">-{item.discount}%</span>
                      )}
                    </div>
                    {isPricingActive(item) && (
                      <span className="active-now-badge">Active Now</span>
                    )}
                    {!item.active && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                  </div>
                  <div className="hierarchy-item-actions">
                    <button
                      type="button"
                      className="btn btn-text btn-sm"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-text btn-sm btn-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="pricing-form-card">
            <h4>{editingId ? 'Edit Special Pricing Period' : 'Add Special Pricing Period'}</h4>

            {error && <div className="form-error">{error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>Special Rate (per night) <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.price || ''}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Discount %</label>
                <input
                  type="number"
                  value={formData.discount || ''}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="0"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>End Date <span className="required">*</span></label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="toggle-row">
              <div className="toggle-label">
                <span>Active</span>
                <span>Enable this special pricing period</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {!showAddForm && (
          <button
            type="button"
            className="btn btn-primary btn-sm hierarchy-add-btn"
            onClick={handleAddNew}
          >
            + Add Special Pricing Period
          </button>
        )}
      </div>

      {/* Effective Rate Summary */}
      <EffectiveSummary
        title="Current Effective Rate"
        items={[]}
        renderItem={() => null}
        description="This is the rate that applies for today's date."
        highlightedValue={
          <span className="effective-rate-value">{formatCurrency(effectiveRate.price)}/night</span>
        }
        highlightedLabel={effectiveRate.description}
        highlightedSource={effectiveRate.source}
      />
    </div>
  );
};

export default SitePricingFormV2;
